import { Injectable } from '@angular/core';

import { LearningApp } from '@app/modules/learning-apps/models/learning-app.model';
import { LearningAppsService } from '@app/modules/learning-apps/services/learning-apps.service';
import { GridDataResult, PageChangeEvent, PagerSettings } from '@progress/kendo-angular-grid';

import { process, SortDescriptor } from '@progress/kendo-data-query';
import { GridPaginationConfig } from 'app/core/config/grid-pagination.config';
import { GridState } from 'app/core/config/grid.state';

import { BehaviorSubject, Observable, of } from 'rxjs';
import { debounceTime, map, switchMap, take, tap } from 'rxjs/operators';

const DELAY          = 10;
const COURSE_COLUMNS = ['Course Name:', 'Activity Unit:', 'Learning Unit:'];
const COURSE_KEYS    = ['courseName', 'activityUnit', 'learningUnit'];
const AVAILABLE      = 'Available';
const NOT_AVAILABLE  = 'Not Available';
const CHIP_SUCCESS   = 'success';
const CHIP_ERROR     = 'error';

@Injectable({providedIn: 'root'})
export class LearningAppsState {
  /** Exposes the state for Learning Apps data as observable */
  public learningApps$: Observable<LearningApp[]>;
  /** Course Column Keys */
  public readonly courseColumnKeys  = COURSE_KEYS;
  /** Course Column Names */
  public readonly courseColumnNames = COURSE_COLUMNS;
  private readonly _isLoading = new BehaviorSubject(true);
  public isLoading$ = this._isLoading.asObservable();
  private readonly _learningAppsGridData = new BehaviorSubject<GridDataResult>(null);
  /** Exposes the state for Learning Apps data لأقهي as observable */
  public learningAppsGridData$ = this._learningAppsGridData.asObservable();
  private readonly gridState = new BehaviorSubject(GridState);
  /** Observable containing the grid state info */
  public gridState$          = this.gridState.asObservable();
  /** Holds the state for Learning Apps data */
  private readonly learningApps     = new BehaviorSubject<LearningApp[]>([]);
  private readonly paginationConfig = new BehaviorSubject<PagerSettings>(GridPaginationConfig);
  /** Observable containing the pagination configuration */
  public paginationConfig$          = this.paginationConfig.asObservable();

  constructor(private readonly learningAppsService: LearningAppsService) {
   this.learningApps$ = this.learningApps.pipe(debounceTime(DELAY), switchMap(res => {
      if (res.length) {
        return of(res);
      } else {
        return this.data;
      }
    }));

   this.learningApps$.subscribe(data => {
     this.loadGridData(data);
   });
  }

  /** Loads data for learning app */
  private get data() {
    return this.learningAppsService.getLearningApps()
      .pipe(take(1), map(res => res.map(item => this.mapStatusAndTheme(item))),
        tap(res => this.learningApps.next(res)));
  }

  /** Triggers to update sorting state and fetch data accordingly. */
  public onDataSortChange(sort: SortDescriptor[]): void {
    const data = this.learningApps.value;
    this.gridState.next({...this.gridState.value, sort});
    this._learningAppsGridData.next(data ? process(this.learningApps.value, this.gridState.value) : null);
  }

  /** Triggers to update page state and fetch data accordingly. */
  public onPageChange(event: PageChangeEvent): void {
    const data = this.learningApps.value;
    this.gridState.next({...this.gridState.value, skip: event.skip, take: event.take});
    this._learningAppsGridData.next(data ? process(this.learningApps.value, this.gridState.value) : null);
  }

  private loadGridData(data: LearningApp[]): void {
    const gridData = data ? process(data, this.gridState.value) : null;
    this._learningAppsGridData.next(gridData);
    this._isLoading.next(false);
  }

  /** Updates the theme and status fields to help render the table */
  private mapStatusAndTheme(item: LearningApp): LearningApp {
    item.timeIntegrationTheme          = item.timeIntegration ? CHIP_SUCCESS : CHIP_ERROR;
    item.timeIntegrationStatus         = item.timeIntegration ? AVAILABLE : NOT_AVAILABLE;
    item.productivityIntegrationTheme  = item.productivityIntegration ? CHIP_SUCCESS : CHIP_ERROR;
    item.productivityIntegrationStatus = item.productivityIntegration ? AVAILABLE : NOT_AVAILABLE;
    return item;
  }
}
